/**
 * Scan Agent Layout
 *
 * Left sidebar nav with collapsible hamburger on mobile.
 * Matches admin layout pattern.
 *
 * Access: scanning_agent OR admin roles only
 */

import React, { useState, useRef } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useClickOutside } from '@/hooks/useClickOutside';
import {
  BookOpen,
  LogOut,
  Loader2,
  ClipboardList,
  LayoutDashboard,
  Library,
  Store,
  MapPin,
  ClipboardCheck,
  Menu,
  X,
  UserCheck,
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
}

export function ScanAgentLayout() {
  const { user, profile, loading, profileLoaded, isAdmin, isScanningAgent, signOut } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  useClickOutside(drawerRef, () => setSidebarOpen(false));

  if (loading || !profileLoaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isScanningAgent() && !isAdmin()) return <Navigate to="/" replace />;

  // Ordered: Add Books → Assign Agent → Pending Agent → Review Queue → rest
  const navItems: NavItem[] = [
    { path: '/scan-agent/bookstore', label: 'Add Books', icon: Store, exact: true },
    { path: '/scan-agent/scan-queue', label: 'Assign Agent', icon: ClipboardList, exact: true },
    { path: '/scan-agent/pending-agent', label: 'Pending Agent', icon: UserCheck, exact: true },
    { path: '/scan-agent/review', label: 'Review Queue', icon: ClipboardCheck, exact: true },
    { path: '/scan-agent', label: 'Content Pipeline', icon: ClipboardList, exact: true },
    { path: '/scan-agent/books', label: 'Books', icon: BookOpen, exact: true },
    { path: '/scan-agent/library', label: 'Global Library', icon: Library, exact: true },
    { path: '/scan-agent/library-lookup', label: 'Library Lookup', icon: MapPin, exact: true },
  ];

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const userInitial = (profile?.name || 'A')[0].toUpperCase();

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b border-[#1A1A1A]/10">
        <Link to="/scan-agent" className="flex items-center gap-3" onClick={() => isMobile && setSidebarOpen(false)}>
          <div className="w-9 h-9 bg-[#111111]/10 rounded-xl flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-[#1A1A1A]" />
          </div>
          <div>
            <span className="text-sm font-semibold text-[#1C1C1E]">Scan Agent</span>
            <p className="text-[10px] text-[#8E8E93]">Book Processing</p>
          </div>
        </Link>
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[#636366] hover:text-[#1C1C1E] rounded-xl transition-all md:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={isMobile ? () => setSidebarOpen(false) : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active
                  ? 'bg-[#111111]/10 text-[#1A1A1A]'
                  : 'text-[#636366] hover:text-[#1C1C1E] hover:bg-black/[0.03]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </Link>
          );
        })}

        {/* Admin Portal link */}
        {isAdmin() && (
          <>
            <div className="border-t border-black/[0.05] my-2" />
            <Link
              to="/admin"
              onClick={isMobile ? () => setSidebarOpen(false) : undefined}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#1A1A1A] hover:bg-[#111111]/5 transition-all"
            >
              <LayoutDashboard className="w-4 h-4" />
              Admin Portal
            </Link>
          </>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-black/[0.05]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#111111]/10 flex items-center justify-center">
            <span className="text-[#1A1A1A] font-semibold text-sm">{userInitial}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#1C1C1E] truncate">{profile?.name || 'Agent'}</p>
            <p className="text-xs text-[#8E8E93] truncate">
              {isAdmin() ? 'Admin' : 'Scanning Agent'}
            </p>
          </div>
        </div>
        <button
          onClick={() => signOut()}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#636366] hover:text-[#FF3B30] hover:bg-[#FF3B30]/5 rounded-xl transition-all"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between h-14 px-4 md:hidden bg-white/95 backdrop-blur-xl border-b border-black/[0.06]">
        <button
          onClick={() => setSidebarOpen(true)}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center text-[#636366] hover:text-[#1C1C1E] rounded-xl transition-all"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="text-sm font-medium text-[#1C1C1E]">Scan Agent</span>
        <div className="w-8 h-8 rounded-lg bg-[#111111]/10 flex items-center justify-center">
          <span className="text-xs text-[#1A1A1A] font-semibold">{userInitial}</span>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        ref={drawerRef}
        className={`fixed top-0 left-0 h-full w-72 z-50 flex flex-col transform transition-transform duration-300 ease-out bg-white border-r border-black/[0.06] ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:hidden`}
      >
        {sidebarContent}
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col fixed h-full z-20 bg-white border-r border-black/[0.06]">
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-60 h-screen overflow-x-hidden overflow-y-auto pt-14 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default ScanAgentLayout;
